import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import styled, { keyframes } from "styled-components";
import { C, F, EASE, ring } from "../styles";
import { Footer, Global, Grain, Scope, useFonts } from "../components/Layout";
import BlackBanner, { BlackGrain } from "../components/BlackBanner";
import Programacao, { CARD_GRAD, PANEL_GRAD, Dot, IconClipboard, IconPlay, SectionTitle, materialUrl } from "../components/Programacao";
import { EBOOKS, GABARITO_ENEM, GUIA, LIVE, RECORDINGS } from "../data/transmissao";
import { DAY_ORDER, liveSlot, materialAt, nextSlot, playlistIdOf, todayDay, unlockAt, youtubeIdOf } from "../utils/schedule";
import { useNow } from "../utils/time";
import logoSos from "../assets/logo-sos-3d.webp";
import logoAprova from "../assets/logo-aprova-branca.svg";
import kit from "../assets/transmissao/kit-sobrevivencia.png";

const useMobile = (query = "(max-width: 759px)") => {
  const get = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia(query).matches;
  const [match, setMatch] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addListener(on);
    return () => mq.removeListener(on);
  }, [query]);
  return match;
};

const float = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-4px); }
`;

const slide = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-60px); }
`;

const Page = styled.div`
  overflow-x: clip;
  background: ${C.blue};
  color: ${C.white};
  min-height: 100vh;
`;

const Hero = styled.header`
  position: relative;
  overflow: hidden;
  background: ${C.ink};
  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  &::before {
    background: radial-gradient(1100px 520px at 50% -10%, rgba(46, 169, 222, 0.35), rgba(46, 169, 222, 0) 70%), linear-gradient(180deg, ${C.navy} 0%, ${C.blue} 100%);
  }
  &::after {
    background: repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.025) 0 1px, transparent 1px 4px);
  }
`;

const Rec = styled.div`
  position: absolute;
  top: clamp(14px, 2vw, 24px);
  right: clamp(14px, 2vw, 28px);
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 11px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  box-shadow: ${ring(1, "rgba(255,255,255,0.12)")};
  font: 700 12px/1 ${F.title};
  font-variant-numeric: tabular-nums;
  color: ${C.white};
  pointer-events: none;
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: clamp(20px, 3vw, 32px) clamp(16px, 4vw, 40px) clamp(28px, 4vw, 40px);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  > img {
    height: 20px;
    width: auto;
    display: block;
    opacity: 0.95;
  }
`;

const LogoFloat = styled.div`
  position: relative;
  width: min(42%, 160px);
  margin: 0 auto;
  animation: ${float} 3.2s ease-in-out infinite alternate;
  img {
    width: 100%;
    height: auto;
    display: block;
    filter: drop-shadow(0 14px 30px rgba(0, 0, 0, 0.35));
  }
  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  .wave {
    animation: ${slide} 2.33s linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    .wave {
      animation: none;
    }
  }
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 15px;
  border-radius: 20px;
  background: rgba(7, 10, 51, 0.82);
  box-shadow: ${(p) => ring(1, p.$live ? C.orange : "rgba(255,255,0,0.6)")};
  font: 600 13px/1.25 ${F.title};
  color: ${(p) => (p.$live ? C.white : C.yellow)};
`;

const Stage = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: stretch;
`;

const ReplayBar = styled.div`
  flex: 1 1 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(7, 10, 51, 0.82);
  box-shadow: ${ring(1, "rgba(255,255,0,0.45)")};
  > span:first-child {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font: 600 13px/1.3 ${F.title};
    color: ${C.white};
    em {
      font-style: normal;
      color: ${C.yellow};
    }
  }
`;

const ReplayLive = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 20px;
  background: ${C.orange};
  color: ${C.white};
  font: 700 12px/1 ${F.title};
  letter-spacing: 0.02em;
`;

const Pill = styled.button`
  min-height: 36px;
  padding: 0 14px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  font: ${(p) => (p.$primary ? 700 : 600)} 12.5px/1 ${F.title};
  background: ${(p) => (p.$primary ? C.yellow : "transparent")};
  color: ${(p) => (p.$primary ? C.blue : C.white)};
  box-shadow: ${(p) => (p.$primary ? "none" : ring(1, "rgba(255,255,255,0.35)"))};
  &:hover {
    box-shadow: ${(p) => (p.$primary ? "none" : ring(1, C.white))};
    filter: ${(p) => (p.$primary ? "brightness(1.08)" : "none")};
  }
`;

const Player = styled.div`
  flex: 1 1 640px;
  min-width: min(100%, 300px);
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  background: ${C.ink};
  box-shadow: ${ring(1.5, C.yellow)}, 0 0 0 4px rgba(255, 255, 0, 0.1);
  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const Offline = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
  padding: 20px;
  background: repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.04) 0 1px, transparent 1px 4px);
  > span:first-child {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 15px;
    border-radius: 20px;
    background: rgba(7, 10, 51, 0.82);
    box-shadow: ${ring(1, C.orange)};
    font: 600 13px/1.25 ${F.title};
    color: ${C.white};
  }
  > strong {
    color: ${C.white};
    font: 700 clamp(18px, 2.2vw, 26px) / 1.2 ${F.title};
    letter-spacing: -0.03em;
    max-width: 24ch;
    text-wrap: balance;
  }
`;

const Chat = styled.aside`
  flex: 0 1 400px;
  min-width: min(100%, 320px);
  min-height: 220px;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  background: ${PANEL_GRAD};
  box-shadow: ${ring(1, "rgba(255,255,255,0.14)")};
  // Abaixo do player (celular e tablet): altura fixa pra caber mensagens e o campo do chat.
  @media (max-width: 1145px) {
    flex-basis: 100%;
    height: clamp(380px, 62vh, 520px);
  }
  > div:first-child {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    strong {
      color: ${C.white};
      font: 700 14px/1 ${F.title};
    }
    small {
      font: 600 11px/1 ${F.title};
      letter-spacing: 0.1em;
      color: ${C.light};
    }
  }
  // Chat no tema escuro do YouTube (dark_theme=1), com o mesmo fundo dele.
  iframe {
    flex: 1;
    width: 100%;
    border: 0;
    background: #0f0f0f;
    color-scheme: dark;
  }
  p {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 24px;
    text-align: center;
    font-size: 14px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.75);
  }
`;

const Shortcuts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 4px;
`;

const Shortcut = styled.a`
  flex: 1 1 220px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 16px;
  text-decoration: none;
  color: ${(p) => (p.$primary ? C.blue : C.white)};
  background: ${(p) => (p.$primary ? C.yellow : CARD_GRAD)};
  box-shadow: ${(p) => (p.$primary ? "none" : ring(1, "rgba(255,255,255,0.14)"))};
  transition: transform 240ms ${EASE};
  &:hover {
    color: ${(p) => (p.$primary ? C.blue : C.white)};
    transform: translateY(-3px);
  }
  > span:first-child {
    flex: none;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => (p.$primary ? C.blue : "rgba(7,10,51,0.82)")};
  }
  strong {
    display: block;
    font: 700 16px/1.2 ${F.title};
  }
  small {
    display: block;
    margin-top: 3px;
    font: 500 12.5px/1.3 ${F.body};
    opacity: 0.8;
  }
`;

const Narrow = styled.section`
  max-width: 1096px;
  margin: 0 auto;
  padding: ${(p) => p.$pad || "clamp(32px,5vw,56px) clamp(16px,4vw,48px) 0"};
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const KitTitle = styled(SectionTitle)`
  > span:first-child {
    font-size: clamp(22px, 2.4vw, 30px);
    letter-spacing: -0.035em;
  }
  > img {
    flex: none;
    width: clamp(56px, 6vw, 76px);
    height: auto;
    transform: rotate(-8deg);
    filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.35));
    margin: -14px 0 -10px;
  }
`;

// Guia em destaque: capa à esquerda, texto à direita (empilha no celular).
const Guia = styled.a`
  display: grid;
  grid-template-columns: minmax(0, 340px) 1fr;
  align-items: center;
  gap: clamp(16px, 3vw, 32px);
  padding: 14px clamp(14px, 2vw, 20px) 14px 14px;
  border-radius: 18px;
  text-decoration: none;
  color: ${C.white};
  background: ${CARD_GRAD};
  box-shadow: ${ring(1.5, "rgba(255,255,0,0.55)")};
  transition: transform 240ms ${EASE}, box-shadow 240ms;
  &:hover {
    color: ${C.white};
    transform: translateY(-3px);
    box-shadow: ${ring(1.5, C.yellow)}, 0 0 0 4px rgba(255, 255, 0, 0.12);
  }
  img {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 1170 / 827;
    border-radius: 10px;
    box-shadow: 0 14px 28px -12px rgba(0, 0, 0, 0.6);
  }
  div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  small {
    padding: 4px 10px;
    border-radius: 20px;
    background: ${C.yellow};
    font: 700 11px/1.2 ${F.title};
    letter-spacing: 0.08em;
    color: ${C.ink};
  }
  strong {
    font: 700 clamp(20px, 2.2vw, 26px) / 1.15 ${F.title};
    letter-spacing: -0.03em;
  }
  p {
    margin: 0;
    max-width: 520px;
    font: 500 14.5px/1.5 ${F.body};
    color: rgba(255, 255, 255, 0.82);
  }
  span {
    margin-top: 4px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font: 700 14px/1 ${F.title};
    color: ${C.yellow};
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    padding: 12px 12px 18px;
    div {
      padding: 0 4px;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const Ebooks = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
  gap: 14px;
`;

const Ebook = styled.a`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  color: ${C.white};
  background: ${CARD_GRAD};
  box-shadow: ${ring(1, "rgba(255,255,255,0.14)")};
  transition: transform 240ms ${EASE}, box-shadow 240ms;
  &:hover {
    color: ${C.white};
    transform: translateY(-3px);
    box-shadow: ${ring(1.5, C.yellow)}, 0 0 0 4px rgba(255, 255, 0, 0.12);
  }
  &:hover .cover img {
    transform: scale(1.03);
  }
  &:hover .cover.vertical img {
    transform: translateY(-3px) rotate(-1.5deg);
  }
  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:hover .cover img {
      transform: none;
    }
  }
`;

// Capa em 16:9 (o formato das horizontais, que preenchem tudo). As verticais
// ficam em pé, como livro, sobre um fundo azul-céu com brilho no centro.
const Cover = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: ${C.ink};
  &.vertical {
    background: radial-gradient(70% 90% at 50% 45%, rgba(108, 203, 239, 0.55), rgba(46, 169, 222, 0) 70%),
      repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.04) 0 1px, transparent 1px 4px), ${C.navy};
  }
  img {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 320ms ${EASE};
  }
  &.vertical img {
    width: auto;
    height: 84%;
    margin: 8% auto 0;
    aspect-ratio: auto;
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 14px 28px -10px rgba(0, 0, 0, 0.6), 0 2px 6px rgba(0, 0, 0, 0.35);
  }
`;

const EbookBody = styled.div`
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  small {
    align-self: flex-start;
    padding: 4px 9px;
    border-radius: 20px;
    background: rgba(7, 10, 51, 0.6);
    box-shadow: ${ring(1, "rgba(108,203,239,0.45)")};
    font: 600 11px/1.2 ${F.title};
    letter-spacing: 0.04em;
    color: ${C.light};
  }
  strong {
    font: 700 16px/1.25 ${F.title};
    letter-spacing: -0.02em;
  }
  span {
    margin-top: auto;
    padding-top: 4px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font: 700 13px/1 ${F.title};
    color: ${C.yellow};
  }
`;

const GabCard = styled.a`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px 24px;
  padding: clamp(20px, 3vw, 28px) clamp(20px, 3vw, 32px);
  border-radius: 20px;
  overflow: hidden;
  text-decoration: none;
  color: ${C.white};
  background: ${CARD_GRAD};
  box-shadow: ${ring(1.5, C.yellow)}, 0 0 0 4px rgba(255, 255, 0, 0.1);
  transition: transform 240ms ${EASE};
  &:hover {
    color: ${C.white};
    transform: translateY(-3px);
  }
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.03) 0 1px, transparent 1px 4px);
  }
  > * {
    position: relative;
  }
  > i {
    flex: none;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${C.yellow};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  > div {
    flex: 1 1 320px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  > div > em {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 13px;
    border-radius: 20px;
    background: rgba(7, 10, 51, 0.82);
    box-shadow: ${ring(1, "rgba(255,255,0,0.6)")};
    font: 600 12px/1.25 ${F.title};
    font-style: normal;
    color: ${C.yellow};
  }
  > div > strong {
    font: 700 clamp(20px, 2.4vw, 28px) / 1.15 ${F.title};
    letter-spacing: -0.035em;
    text-wrap: balance;
  }
  > div > span {
    font-size: 15px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.85);
  }
  > b {
    flex: none;
    display: inline-flex;
    align-items: center;
    min-height: 50px;
    padding: 0 22px;
    border-radius: 12px;
    background: ${C.orange};
    color: ${C.white};
    font: 700 15px/1 ${F.title};
  }
`;

const BlackBand = styled.section`
  position: relative;
  margin-top: clamp(40px, 6vw, 72px);
  overflow: hidden;
  background-color: ${C.graphite};
  background-image: linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 30px 30px;
  border-top: 2px solid ${C.orange};
  border-bottom: 2px solid ${C.orange};
  > div {
    position: relative;
    max-width: 1096px;
    margin: 0 auto;
    padding: clamp(28px, 4vw, 48px) clamp(16px, 4vw, 48px) 0;
  }
`;

const BackToPlayer = styled.button`
  position: fixed;
  right: 14px;
  bottom: 16px;
  z-index: 60;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 18px;
  border-radius: 24px;
  border: none;
  cursor: pointer;
  background: ${C.yellow};
  color: ${C.blue};
  font: 700 14px/1 ${F.title};
  box-shadow: 0 10px 26px -8px rgba(0, 0, 0, 0.6);
`;

const IconBook = ({ color = C.yellow, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5z M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5" />
  </svg>
);

const IconDownload = ({ color = C.yellow, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12 M7 10l5 5 5-5 M5 21h14" />
  </svg>
);

const IconCalendar = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.yellow} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 2v4 M16 2v4 M3 10h18 M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
  </svg>
);

const WAVE =
  "M600 363 q15 -18 30 0 q15 18 30 0 q15 -18 30 0 q15 18 30 0 q15 -18 30 0 q15 18 30 0 q15 -18 30 0 q15 18 30 0 q15 -18 30 0 q15 18 30 0 q15 -18 30 0 q15 18 30 0 q15 -18 30 0 q15 18 30 0 q15 -18 30 0 q15 18 30 0 q15 -18 30 0 q15 18 30 0";

const pad = (n) => String(Math.floor(n)).padStart(2, "0");

export default function TransmissaoPage() {
  const history = useHistory();
  const now = useNow();
  const mobile = useMobile();
  const heroRef = useRef(null);
  const [heroOut, setHeroOut] = useState(false);
  const [loadedAt] = useState(() => Date.now());
  // Gravação em exibição no player: { id } | { src } | { missing }, sempre com label.
  const [replay, setReplay] = useState(null);
  useFonts();

  useEffect(() => {
    document.title = "Ao vivo | SOS Aprova";
    const onScroll = () => {
      const el = heroRef.current;
      setHeroOut(!!el && el.getBoundingClientRect().bottom < 80);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toPlayer = () => {
    const el = heroRef.current;
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY, behavior: "smooth" });
  };

  const live = liveSlot(now);
  const next = nextSlot(now);
  // No dia de aulão, a live do dia fica no player (com o chat) desde a 00h até
  // o fim do último aulão: antes do início aparece a sala de espera do YouTube,
  // que já tem chat. Fora disso, o vídeo fixo (se houver).
  const liveDay = todayDay(now);
  const onAir = live || (liveDay && now < unlockAt(liveDay) ? liveDay : null);
  const liveId = (onAir && youtubeIdOf(RECORDINGS[onAir.date])) || youtubeIdOf(LIVE.youtubeId);
  const channelId = (LIVE.channelId || "").trim();
  const playlistId = playlistIdOf(LIVE.playlistId);

  const watch = (day) => {
    const label = `${day.dow.toLowerCase()} ${day.label}`;
    const rid = youtubeIdOf(RECORDINGS[day.date]);
    if (rid) setReplay({ id: rid, label });
    else if (playlistId) setReplay({ src: `https://www.youtube.com/embed/videoseries?list=${playlistId}&index=${DAY_ORDER.indexOf(day.date) + 1}&autoplay=1&rel=0`, label });
    else setReplay({ missing: true, label });
    toPlayer();
  };

  const playingId = (replay && replay.id) || liveId;
  let videoSrc = "";
  if (replay && replay.src) videoSrc = replay.src;
  else if (replay && replay.missing) videoSrc = "";
  else if (playingId) videoSrc = `https://www.youtube.com/embed/${playingId}?autoplay=1&rel=0`;
  else if (live && channelId) videoSrc = `https://www.youtube.com/embed/live_stream?channel=${channelId}&autoplay=1`;
  const chatSrc = !replay && liveId ? `https://www.youtube.com/live_chat?v=${liveId}&embed_domain=${window.location.hostname}&dark_theme=1` : "";

  const offlineMsg = replay && replay.missing ? `A gravação de ${replay.label} entra no ar aqui em breve.` : "Fica nessa frequência: o próximo aulão entra no ar aqui.";
  const statusTitle = live
    ? `${live.time} · ${live.prof} · ${live.disc}`
    : next
    ? `${next.dow} ${next.label} · ${next.time} · ${next.prof}`
    : "Valeu por ficar na frequência!";

  const today = todayDay(now);
  const todayOpen = !!today && now >= materialAt(today);
  const todayMaterial = todayOpen ? materialUrl(today.date) : "";
  const todaySub = todayOpen ? "Baixe e revise junto com a aula" : "Libera no dia de cada aulão";

  const rec = Math.floor((Date.now() - loadedAt) / 1000) + 165;

  return (
    <Page>
      <Global />
      <Hero ref={heroRef}>
        <Rec aria-hidden="true">
          <Dot size={7} blink />
          REC {pad(rec / 3600)}:{pad((rec % 3600) / 60)}:{pad(rec % 60)}
        </Rec>
        <HeroInner>
          <Brand>
            <img src={logoAprova} alt="aprova total" />
            <LogoFloat>
              <img src={logoSos} alt="SOS Aprova" />
              <Scope clipId="sos-hero-freq" strokeWidth={7}>
                <g className="wave">
                  <path d={WAVE} fill="none" stroke="#FFFFFF" strokeOpacity="0.3" strokeWidth="7" strokeLinecap="round" />
                </g>
              </Scope>
            </LogoFloat>
            <Status $live={!!live}>
              <Dot blink />
              {live ? "NO AR AGORA" : "PRÓXIMO AULÃO"} · {statusTitle}
            </Status>
          </Brand>

          <Stage>
            {replay && (
              <ReplayBar>
                <span>
                  <IconPlay color={C.yellow} />
                  Gravação de <em>{replay.label}</em>
                </span>
                {live && (
                  <ReplayLive>
                    <Dot size={7} color={C.white} blink />
                    Tem aulão no ar agora
                  </ReplayLive>
                )}
                <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Pill type="button" onClick={() => document.getElementById("programacao").scrollIntoView({ behavior: "smooth" })}>
                    ↓ Voltar à programação
                  </Pill>
                  <Pill type="button" $primary onClick={() => setReplay(null)}>
                    {live ? "Ir pro ao vivo" : "Voltar pro ao vivo"}
                  </Pill>
                </span>
              </ReplayBar>
            )}
            <Player>
              {videoSrc ? (
                <iframe
                  key={videoSrc}
                  src={videoSrc}
                  title="Aulão SOS ao vivo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <Offline>
                  <span>
                    <Dot />
                    SINAL EM BREVE
                  </span>
                  <strong>{offlineMsg}</strong>
                </Offline>
              )}
            </Player>
            <Chat>
              <div>
                <strong>Chat do aulão</strong>
                <small>YOUTUBE</small>
              </div>
              {chatSrc ? <iframe src={chatSrc} title="Chat do YouTube" /> : <p>Quando o aulão entrar no ar, manda sua dúvida aqui. Os professores respondem ao vivo.</p>}
            </Chat>
          </Stage>

          <Shortcuts>
            <Shortcut
              $primary
              href={todayMaterial || "#programacao"}
              {...(todayMaterial ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              <span>
                <IconDownload />
              </span>
              <span>
                <strong>Material de hoje</strong>
                <small>{todaySub}</small>
              </span>
            </Shortcut>
            {GUIA.url && (
              <Shortcut href={GUIA.url} target="_blank" rel="noopener noreferrer">
                <span>
                  <IconBook />
                </span>
                <span>
                  <strong>Guia de sobrevivência</strong>
                  <small>Seu mapa pra reta final</small>
                </span>
              </Shortcut>
            )}
            <Shortcut
              href="/simulados"
              onClick={(e) => {
                e.preventDefault();
                history.push("/simulados");
              }}
            >
              <span>
                <IconClipboard />
              </span>
              <span>
                <strong>Simulados</strong>
                <small>Teste o que você revisou</small>
              </span>
            </Shortcut>
            <Shortcut href="#programacao">
              <span>
                <IconCalendar />
              </span>
              <span>
                <strong>Programação</strong>
                <small>Perdeu um aulão? Assista de novo</small>
              </span>
            </Shortcut>
          </Shortcuts>
        </HeroInner>
      </Hero>

      <Programacao now={now} mobile={mobile} onWatch={watch} />

      <Narrow id="kit">
        <KitTitle>
          <span>Kit de sobrevivência SOS</span>
          <img src={kit} alt="" aria-hidden="true" />
          <i />
        </KitTitle>
        {GUIA.url && (
          <Guia href={GUIA.url} target="_blank" rel="noopener noreferrer">
            <img src={GUIA.cover} alt="Capa do Guia SOS Aprova" loading="lazy" />
            <div>
              <small>GUIA DO SOS</small>
              <strong>{GUIA.title}</strong>
              <p>{GUIA.text}</p>
              <span>
                <IconDownload size={16} />
                Baixar guia
              </span>
            </div>
          </Guia>
        )}
        <Ebooks>
          {EBOOKS.map((e) => (
            <Ebook key={e.url || e.title} href={e.url || "#kit"} {...(e.url ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
              <Cover className={`cover${e.vertical ? " vertical" : ""}`}>
                {e.cover && <img src={e.cover} alt={`Capa: ${e.title}: ${e.area}`} loading="lazy" />}
              </Cover>
              <EbookBody>
                <small>{e.area}</small>
                <strong>{e.title}</strong>
                <span>
                  <IconDownload size={16} />
                  Baixar PDF
                </span>
              </EbookBody>
            </Ebook>
          ))}
        </Ebooks>
      </Narrow>

      <Narrow $pad="clamp(24px,4vw,40px) clamp(16px,4vw,48px) 0">
        <GabCard href={GABARITO_ENEM.url} target="_blank" rel="noopener noreferrer">
          <i>
            <IconPlay color={C.blue} size={24} />
          </i>
          <div>
            <em>
              <Dot size={7} />
              {GABARITO_ENEM.liberado ? "LIBERADO" : "LIBERADO NO DOMINGO"}
            </em>
            <strong>Fez a prova? Confere o gabarito comentado</strong>
            <span>Os professores do Aprova resolvem o Enem questão por questão, ao vivo no YouTube.</span>
          </div>
          <b>Assistir à correção&nbsp;→</b>
        </GabCard>
      </Narrow>

      <BlackBand>
        <BlackGrain />
        <div>
          <BlackBanner bare />
        </div>
      </BlackBand>

      <Footer>
        <img src={logoAprova} alt="aprova total" />
        <span>Conhecimento máximo. Tempo mínimo.</span>
      </Footer>

      {mobile && heroOut && !!videoSrc && (
        <BackToPlayer type="button" onClick={toPlayer}>
          <IconPlay />
          Voltar ao player
        </BackToPlayer>
      )}
      <Grain />
    </Page>
  );
}
